/* eslint-disable @typescript-eslint/naming-convention */
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, iif, Observable, of, Subscription, throwError } from 'rxjs';
import {
  AbstractControl,
  FormArray,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { faStar } from '@fortawesome/free-solid-svg-icons/faStar';
import { concatMap, filter, mergeMap, skip, take } from 'rxjs/operators';
import { ThemePalette } from '@angular/material/core';
import { RequestBodyData } from '../../../models/request-body.data';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { PayloadInterface } from '../../../models/interface/payload.interface';
import { InterventionInstanceConfigurationInterface } from '../../../models/interface/intervention-instances/intervention-instance.configuration.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { InterventionConfigurationInterface } from '../../../models/interface/intervention-configuration.interface';
import { UserInterface } from '../../../models/interface/user.interface';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { LessonInterface } from '../../../models/interface/lesson.interface';
import { SkillInterface } from '../../../models/interface/skill.interface';
import { Store } from '@ngrx/store';
import { StudyActionTypes } from '../../../store/study/study.action';
import { DiaryStore } from '../../../store/diary/component-store/diary.store';
import { InterventionInstanceStore } from '../../../store/intervention-instance/component-store/intervention-instance.store';
import { QuestionnaireStore } from '../../../store/lesson-questionnaire/component-store/lesson-questionnaire.store';
import { SkillStore } from '../../../store/skill/component-store/skill.store';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { UserStore } from '../../../store/user/component-store/user.store';
import { HttpResponse } from '@angular/common/http';

const noSelectedCollaboratorError = 'noSelectedCollaborators';

function noSelectedCollaboratorValidator(): ValidatorFn {
  return (formArray: AbstractControl): ValidationErrors | null => {
    const collaboratorList = formArray as FormArray;
    const noneSelected = collaboratorList.controls.every((control) => !control.value);
    return noneSelected ? { [noSelectedCollaboratorError]: true } : null;
  };
}

type View =
  | 'setQuestionnaireConfiguration'
  | 'resetQuestionnaireConfig'
  | 'showInterventionType'
  | 'editQuestionnaireConfiguration'
  | 'setInitialQuestionnaireConfigControl'
  | 'setInstanceQuestionnaireConfig';

type ResponseState = 'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE';

const GUIDANCE_TYPES = ['UNGUIDED', 'GUIDED'] as const;
type GuidanceType = typeof GUIDANCE_TYPES[number];

@Component({
  selector: 'app-instance-update-form',
  templateUrl: './instance-update-form.component.html',
  styleUrls: ['./instance-update-form.component.scss'],
  providers: [DiaryStore, InterventionInstanceStore, QuestionnaireStore, SkillStore, UserStore],
})
export class InstanceUpdateFormComponent implements OnInit, OnDestroy {
  // Emit payload of instance to parent component
  @Output()
  confirmedForm = new EventEmitter<PayloadInterface>();

  // Icon
  faStar = faStar;

  // Intervention and its default configurations
  public intervention: InterventionInterface;

  // Element id
  public elementId: string;

  // Created instance
  public instance: InterventionInstanceInterface;

  // Questionnaires of study
  public questionnaires: LessonInterface[] = [];

  // Skills of intervention
  public skills: SkillInterface[] = [];

  // Diary
  public diaries: DiaryInterface[] = [];
  public diariesOfStudy$: Observable<DiaryInterface[]>;

  // MyUserId
  public myUserId: number;

  // Form select options
  public studyMembers: UserInterface[] = [];
  public studyCollaborator: UserInterface[] = [];
  public selectableCollaborators: UserInterface[] = [];

  // Locked questionnaires (orders cannot be changed)
  public lockedQuestionnaireIds: number[] = [];

  public intervention$: Observable<InterventionInterface | undefined>;

  // Instance creation studyForm
  public instanceFormType: UntypedFormGroup;
  public currentDate = this.helperService.formatDateToYYYY_MM_DD_THH_MM(Date.now());

  // Payload for instance
  public payload: PayloadInterface;

  public updateView = new BehaviorSubject<View | null>(null);

  // Subject and Observable
  public formCustomOrderSubject = new BehaviorSubject<{ questionnaire: number; enabled: boolean }[]>([]);
  public formCustomOrder$ = this.formCustomOrderSubject.asObservable();

  public guidanceTypes = GUIDANCE_TYPES;

  public updateECoachResponse = new BehaviorSubject<ResponseState>('DEFAULT');

  updateECoachResponse$: Observable<ResponseState>;

  // Data from StudyService
  public collaborators: UserInterface[];

  public isManager$: Observable<boolean>;

  // Selected guiding type
  public selectedGuidanceType: GuidanceType = 'UNGUIDED';

  public positionSubject = new BehaviorSubject<boolean[]>([]);

  public skillsOfIntervention$: Observable<SkillInterface[]>;

  // Ngx datetimepicker
  public showSpinners = false;
  public showSeconds = false;
  public touchUi = false;
  public enableMeridian = false;
  public minDate: Date;
  public stepHour = 1;
  public stepMinute = 1;
  public stepSecond = 1;
  public color: ThemePalette = 'primary';

  private defaultConfiguration: {
    questionnaire_configuration: InterventionConfigurationInterface[];
    custom_order: number[];
  };
  private defaultConfigurationReadOnly: {
    questionnaire_configuration: InterventionConfigurationInterface[];
    custom_order: number[];
  };

  private instance$: Observable<InterventionInstanceInterface>;

  private questionnairesOfIntervention$: Observable<LessonInterface[]>;

  private addEcoachesToInterventionInstanceResponse$: Observable<any>;
  private deleteEcoachesToInterventionInstanceResponse$: Observable<any>;

  private collaboratorsOfStudy$: Observable<{ studyId: number; collaborators: UserInterface[] }>;

  private selectedIsInterventionTypeSubject = new BehaviorSubject<GuidanceType>('UNGUIDED');

  private isManagerSubject = new BehaviorSubject<boolean>(false);

  private subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private helperService: HelperService,
    private interventionInstanceStore: InterventionInstanceStore,
    private diaryStore: DiaryStore,
    private questionnaireStore: QuestionnaireStore,
    private skillStore: SkillStore,
    private userStore: UserStore,
    private store: Store<{
      skills: SkillInterface[];
      getCollabInterventionById: InterventionInterface;
      getCollaboratorsByStudyId: { studyId: number; collaborators: UserInterface[] };
    }>,
  ) {
    this.isManager$ = this.isManagerSubject.asObservable();
    // Form type contains searchFilter options
    this.instanceFormType = this.formBuilder.group({
      collaborator: [null, Validators.required],
      patient: ['', Validators.required],
      intervention_type: [{ value: '', disabled: true }, Validators.required],
      starts_at: ['', Validators.required],
      questionnaire_configuration: this.formBuilder.array([]),
      collaboratorList: new UntypedFormArray([], noSelectedCollaboratorValidator()),
    });
    this.diariesOfStudy$ = this.diaryStore.diariesOfStudy$;
    this.instance$ = this.interventionInstanceStore.instance$;
    this.questionnairesOfIntervention$ = this.questionnaireStore.questionnairesOfIntervention$;
    this.skillsOfIntervention$ = this.skillStore.skillsOfIntervention$;
    this.addEcoachesToInterventionInstanceResponse$ = this.userStore.addEcoachesToInterventionInstanceResponse$;
    this.deleteEcoachesToInterventionInstanceResponse$ = this.userStore.deleteEcoachesToInterventionInstanceResponse$;
    this.updateECoachResponse$ = this.updateECoachResponse.asObservable();
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  get f() {
    return this.instanceFormType.controls;
  }

  public get fCollaboratorList(): UntypedFormArray {
    return this.instanceFormType.get('collaboratorList') as UntypedFormArray;
  }

  public get fQuestionnaireConfiguration() {
    return this.instanceFormType.get('questionnaire_configuration') as UntypedFormArray;
  }

  @Input()
  set _id(_id: string) {
    this.elementId = _id;
  }

  @Input()
  set _intervention(_intervention: InterventionInterface) {
    this.intervention = _intervention;
    this.selectedGuidanceType = this.intervention.attributes.intervention_type === 'accompanied' ? 'GUIDED' : 'UNGUIDED';
    this.defaultConfiguration = this.intervention.attributes.default_configuration;
    this.defaultConfigurationReadOnly = JSON.parse(JSON.stringify(_intervention.attributes.default_configuration));
  }

  @Input()
  set _members(_members: UserInterface[]) {
    this.studyMembers = _members;
  }

  @Input()
  set _collaborators(_collaborators: UserInterface[]) {
    if (_collaborators) {
      this.studyCollaborator = _collaborators;
    }
  }

  @Input()
  set _instance(_instance: InterventionInstanceInterface) {
    this.instance = _instance;
    this.setMinDate();
  }

  @Input()
  set _questionnaires(_questionnaires: LessonInterface[]) {
    if (_questionnaires !== undefined) {
      this.questionnaires = _questionnaires;
      this.setInitialQuestionnaireConfigControl(this.questionnaires);
      this.setInstanceQuestionnaireConfig();
    }
  }

  @Input()
  set _myUserId(_myUserId: number) {
    this.myUserId = _myUserId;
  }

  ngOnInit(): void {
    // Role per study
    this.store.dispatch({
      type: StudyActionTypes.getCollaboratorsType,
      payload: { studyId: this.intervention.attributes.study_id, include: 'roles' },
    });

    this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(this.intervention.attributes.study_id));
    this.subscriptions.push(
      this.collaboratorsOfStudy$.pipe(skip(1), take(1)).subscribe({
        next: (result) => {
          this.collaborators = this.helper.excludeUsersWithOnlyStudyAccess(result.collaborators);
          // Determine my highest role
          const myself: UserInterface = this.collaborators.find((user) => user.id.toString() === this.myUserId.toString());
          if (myself !== undefined) {
            const myroleSlug = this.helperService.getHighestRoleOfCollaborator(myself);
            this.isManagerSubject.next(!!myroleSlug.match(/study\.(ecoachmanager|owner)$/));
          }

          this.addCheckboxes();
        },
        error: (_error) => {
          this.isManagerSubject.next(false);
        },
      }),
    );

    this.interventionInstanceStore.getInstance(this.instance.id);
    this.subscriptions.push(
      this.instance$
        .pipe(
          filter((value) => !!value),
          take(1),
        )
        .subscribe((result: InterventionInstanceInterface) => {
          this.instance = result;
          this.getProgress();
          this.currentDate = this.helperService.formatDateToYYYY_MM_DD_THH_MM(this.instance.attributes.configuration.starting_at * 1000);

          // Set values and date for group creation studyForm
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
        }),
    );

    this.subscriptions.push(
      this.translateService.onLangChange.subscribe((_event: LangChangeEvent) => {
        this.setMinDate();
        this.subscriptions.push(this.setQuestionnaireConfiguration(this.intervention.id));
      })
    );
    this.setMinDate();

    // Diary
    this.diaryStore.getDiariesOfStudy({ studyId: this.intervention.attributes.study_id });
    this.subscriptions.push(
      this.diariesOfStudy$.subscribe((result) => {
        this.diaries = result;
      })
    );

    this.skillStore.getSkillsOfIntervention({ id: this.intervention.id });
    this.subscriptions.push(
      this.skillsOfIntervention$.subscribe((result) => {
        this.skills = result;
      })
    );
  }

  // Set questionnaire configuration
  public setQuestionnaireConfiguration(interventionId: number): Subscription {
    this.questionnaireStore.getQuestionnairesIntervention({ interventionId });
    return this.questionnairesOfIntervention$.pipe(skip(1), take(1)).subscribe((result: any) => {
      this.questionnaires = this.helper.sortLessonsAscending(result);
      this.setInitialQuestionnaireConfigControl(this.questionnaires);
      this.setInstanceQuestionnaireConfig();
      this.positionSubject = new BehaviorSubject<boolean[]>(new Array(this.fQuestionnaireConfiguration.length).fill(false));
      this.updateView.next('setQuestionnaireConfiguration');
    });
  }

  resetQuestionnaireConfig() {
    // Custom order from instance
    this.formCustomOrderSubject.next(
      this.instance.attributes.configuration.custom_order
        .map(id => parseInt(id.toString(), 10))
        .map((x: number) => ({ questionnaire: x, enabled: true }))
    );

    // Set collaborator, patient, starts_at and intervention_type from Instance
    const collaborators = this.getMultipleCollaboratorsFromInstance();
    this.instanceFormType.controls['collaborator'].patchValue(collaborators, { onlySelf: true });
    this.instanceFormType.controls['patient'].patchValue(
      this.helperService.findArrObjById(this.instance.attributes.patient_id, this.studyMembers),
      { onlySelf: true },
    );
    this.instanceFormType.controls['starts_at'].patchValue(
      this.helperService.formatDateToYYYY_MM_DD_THH_MM(this.instance.attributes.configuration.starting_at * 1000)
    );
    this.instanceFormType.controls['intervention_type'].patchValue(this.instance.attributes.intervention_type.toUpperCase());

    this.questionnaires.forEach((questionnaire: LessonInterface, i: number) => {
      const foundIndex = this.formCustomOrderSubject.value.findIndex(
        (index) => index.questionnaire.toString() === questionnaire.id.toString(),
      );
      const found = foundIndex > -1;

      // If no configuration found in instance then get default configuration of intervention
      let interventionQuestionnaireConfig: InterventionConfigurationInterface = null;
      let instanceQuestionnaireConfig: InterventionInstanceConfigurationInterface = null;
      let unlockType: string;
      let unlockDaysAfterStart: number;
      let feedbackRequired: boolean;
      let unlockDate: Date;

      const hasInstanceConfig =
        this.getInstanceConfigurationOfQuestionnaire(
          questionnaire.id,
          this.instance.attributes.configuration.questionnaire_configuration
        ) !== null;
      if (hasInstanceConfig) {
        instanceQuestionnaireConfig = this.getInstanceConfigurationOfQuestionnaire(
          questionnaire.id,
          this.instance.attributes.configuration.questionnaire_configuration
        );
        unlockType = this.getUnlockTypeOfFirst(questionnaire.id, instanceQuestionnaireConfig.unlock_type).toUpperCase();
        unlockDaysAfterStart = this.calculateUnlockDaysAfterStart(questionnaire.id, this.instance);
        feedbackRequired = instanceQuestionnaireConfig.feedback_required;
        unlockDate = this.getRecalculatedUnlockDate(questionnaire.id, unlockDaysAfterStart);
      } else {
        interventionQuestionnaireConfig = this.getDefaultConfigurationOfQuestionnaire(
          questionnaire.id,
          this.defaultConfigurationReadOnly.questionnaire_configuration
        );
        unlockType = this.getUnlockTypeOfFirst(questionnaire.id, interventionQuestionnaireConfig.unlock_type).toUpperCase();
        unlockDaysAfterStart = interventionQuestionnaireConfig.unlock_days_after_start;
        feedbackRequired = interventionQuestionnaireConfig.feedback_required;
        unlockDate = this.getRecalculatedUnlockDate(questionnaire.id, unlockDaysAfterStart);
      }

      this.editQuestionnaireConfiguration(
        questionnaire,
        i,
        this.formCustomOrderSubject.value,
        unlockType,
        unlockDaysAfterStart,
        unlockDate,
        feedbackRequired
      );

      if (!found) {
        const added = this.formCustomOrderSubject.value;
        added.push({ questionnaire: parseInt(questionnaire.id.toString(), 10), enabled: found });
        this.formCustomOrderSubject.next(added);
      }
    });
    this.sortByDefaultCustomOrderPosition();
    this.updateView.next('resetQuestionnaireConfig');
  }

  // Group creation studyForm check intervention_type
  public showInterventionType(value: GuidanceType): void {
    if (this.selectedGuidanceType === 'GUIDED' && value === 'UNGUIDED') {
      this.fQuestionnaireConfiguration.controls.forEach(control => {
        if (control.get('unlock_type').value === 'MANUALLY') {
          control.get('unlock_type').patchValue('AFTER_PREVIOUS', { onlySelf: true });
        }
        control.get('feedback_required').patchValue(false, { onlySelf: true });
      });
    }
    this.instanceFormType.controls['intervention_type'].patchValue(this.selectedGuidanceType, { onlySelf: true });
    this.selectedIsInterventionTypeSubject.next(value);
    this.updateView.next('showInterventionType');
  }

  // Submit parameters required to create a group
  public confirmInstanceSettings(): boolean {
    if (this.isInvalidInstanceForm()) {
      const collabIds: number[] = this.instanceFormType
        .get('collaboratorList')
        .value.map((v: boolean, i: number) => (v ? this.selectableCollaborators[i].id : null))
        .filter((x: number | null) => x !== null)
        .map((id: number) => parseInt(id.toString(), 10));
      const startsAt = this.helperService.convertStringToUnixTimestamp(this.f.starts_at.value);
      let interventionType = '';
      switch (this.f.intervention_type.value) {
        case 'UNGUIDED': {
          interventionType = 'Unguided';
          break;
        }
        case 'GUIDED': {
          interventionType = 'Guided';
          break;
        }
        default:
          break;
      }

      const customConfig: InterventionInstanceConfigurationInterface[] = [];
      const customOrder: number[] = this.formCustomOrderSubject.value
        .filter((x) => x.enabled)
        .map((x) => x.questionnaire);

      // Configuration
      this.fQuestionnaireConfiguration.controls.forEach((formcontrol) => {
        const questionnaireId = parseInt(formcontrol.get('questionnaire_id').value, 10);
        const unlockDate = new Date(formcontrol.get('unlock_date').value).toUTCString();
        const unlockType = formcontrol.get('unlock_type').value.toLowerCase();
        const defaultBonusLesson = this.getDefaultBonusLesson(questionnaireId);
        const configBody = {
          id: questionnaireId,
          unlock_type: unlockType,
          unlock_date: this.helperService.convertStringToUnixTimestamp(unlockDate),
          feedback_required: formcontrol.get('feedback_required').value,
          condition: this.getDefaultConditional(questionnaireId),
          bonus_lesson: defaultBonusLesson,
        };
        if (unlockType !== 'conditional') {
          delete configBody.condition;
          delete configBody.bonus_lesson;
        } else if (defaultBonusLesson == null) {
          delete configBody.bonus_lesson;
        }
        // Only add the configuration are needed for the custom_order to custom_config
        if (customOrder.includes(questionnaireId)) {
          customConfig.push(configBody);
        }
      });

      const payload: PayloadInterface = new RequestBodyData('intervention_instance', {
        ecoach_ids: collabIds,
        patient_id: this.instance.attributes.patient_id,
        intervention_id: this.intervention.id,
        intervention_type: interventionType,
        configuration: {
          questionnaire_configuration: customConfig,
          custom_order: customOrder,
          starting_at: startsAt,
        },
      });
      this.payload = payload;
      this.confirmedForm.emit(payload);
      return true;
    } else {
      return false;
    }
  }

  // TODO Check instance studyForm for inconsistency
  public isInvalidInstanceForm(): boolean {
    return true;
  }

  public sortByDefaultCustomOrderPosition(): void {
    this.fQuestionnaireConfiguration.controls.sort((a, b) => {
      if (a.get('current_enabled').value && b.get('current_enabled').value) {
        const indexA = a.get('current_position').value.toString();
        const indexB = b.get('current_position').value.toString();
        return indexA - indexB;
      } else if (!a.get('current_enabled').value && b.get('current_enabled').value) {
        return 1;
      } else if (a.get('current_enabled').value && !b.get('current_enabled').value) {
        return -1;
      } else {
        const questionnaireA = a.get('questionnaire_id').value.toString();
        const questionnaireB = b.get('questionnaire_id').value.toString();
        return (
          this.questionnaires.find((value) => value.id.toString() === questionnaireA.toString()).attributes.position -
          this.questionnaires.find((value) => value.id.toString() === questionnaireB.toString()).attributes.position
        );
      }
    });
    this.fQuestionnaireConfiguration.controls.forEach((formControl, index) => {
      formControl.get('current_position').patchValue(index.toString(), { onlySelf: true });
    });
    const controls: AbstractControl[] = this.fQuestionnaireConfiguration.controls;
    const formCustom: Array<{ questionnaire: number; enabled: boolean }> = [];
    controls.forEach((abc: AbstractControl) => {
      const questionnaireId = parseInt(abc.get('questionnaire_id').value, 10);
      formCustom.push({ questionnaire: questionnaireId, enabled: abc.get('current_enabled').value });
    });
    this.formCustomOrderSubject.next(formCustom);
  }

  public getProgress(): void {
    this.lockedQuestionnaireIds = this.instance.attributes.progress.finished_questionnaires;
  }

  public isFirstNotLocked(questionnaireId: number): boolean {
    for (const value of this.formCustomOrderSubject.value) {
      const qreId = value.questionnaire.toString();
      const found = this.lockedQuestionnaireIds.some((id) => id.toString() === qreId);
      if (!found) {
        return questionnaireId.toString() === qreId;
      }
    }
    return false;
  }

  public getRecalculatedUnlockDate(questionnaireId: number, unlockDaysAfterStart: number): Date {
    const instanceQuestionnaireConfig = this.getInstanceConfigurationOfQuestionnaire(
      questionnaireId,
      this.instance.attributes.configuration.questionnaire_configuration
    );

    if (
      instanceQuestionnaireConfig !== null &&
      this.helper.convertUnixTimestampToString(instanceQuestionnaireConfig.unlock_date) !== 'NO_TIMESTAMP_FOUND'
    ) {
      return new Date(instanceQuestionnaireConfig.unlock_date * 1000);
    } else {
      const startsAt = this.helperService.convertStringToUnixTimestamp(this.f['starts_at'].value);
      const daysToMs = 24 * 60 * 60 * 1000;
      const unlockMsAfterStart = Math.abs(unlockDaysAfterStart) * daysToMs;
      return new Date(startsAt + unlockMsAfterStart);
    }
  }

  noSelectedCollaborator(): boolean {
    return this.fCollaboratorList.hasError(noSelectedCollaboratorError);
  }

  private getRemoveECoachesRequest(removeIds: number[]) {
    if (removeIds.length === 0) {
      return of(null);
    }
    const payloadRemove: PayloadInterface = new RequestBodyData(
      'intervention_instance',
      { ecoach_ids: removeIds },
    );
    this.userStore.deleteEcoachesToInterventionInstance({
      instanceId: this.instance.id,
      payload: payloadRemove,
    });
    return this.deleteEcoachesToInterventionInstanceResponse$.pipe(
      skip(1),
      take(1),
      mergeMap(
        (result) => iif(
          () => result instanceof HttpResponse,
          of(result),
          throwError(() => result),
        ),
      ),
    );
  }

  public updateECoaches(): void {
    if (this.noSelectedCollaborator()) {
      return;
    }

    this.updateECoachResponse.next('LOADING');

    const selectedECoachIDs: number[] = [];
    const collaboratorList: boolean[] = this.instanceFormType.get('collaboratorList').value;
    for (let i = 0; i < collaboratorList.length; i++) {
      if (collaboratorList[i]) {
        selectedECoachIDs.push(this.selectableCollaborators[i].id);
      }
    }
    // This should never happen because of noSelectedCollaborator()
    if (selectedECoachIDs.length === 0) {
      this.updateECoachResponse.next('FAILURE');
      setTimeout(() => {
        this.updateECoachResponse.next('DEFAULT');
      }, 2500);
      return;
    }
    const oldIDs = this.instance.attributes.ecoach_id;
    // Get all old IDs that aren't selected anymore
    const removeIds = oldIDs.filter((id) => selectedECoachIDs.every((foundId) => foundId !== id));
    const payloadAdd: PayloadInterface = new RequestBodyData(
      'intervention_instance',
      { ecoach_ids: selectedECoachIDs },
    );
    this.userStore.addEcoachesToInterventionInstance({
      instanceId: this.instance.id,
      payload: payloadAdd,
    });
    const addRequest = this.addEcoachesToInterventionInstanceResponse$.pipe(
      skip(1),
      take(1),
      mergeMap(
        (result) => iif(
          () => result instanceof HttpResponse,
          of(result),
          throwError(() => result),
        ),
      ),
    );
    this.subscriptions.push(
      addRequest.pipe(concatMap(() => this.getRemoveECoachesRequest(removeIds))).subscribe({
        next: () => {
          this.updateECoachResponse.next('SUCCESS');
          setTimeout(() => {
            this.updateECoachResponse.next('DEFAULT');
          }, 2500);
        },
        error: () => {
          this.updateECoachResponse.next('FAILURE');
          setTimeout(() => {
            this.updateECoachResponse.next('DEFAULT');
          }, 2500);
        },
      }),
    );
  }

  public showUnlockHint(questionnaireId: number): Observable<boolean> {
    return this.updateView.pipe(
      mergeMap(() => {
        const qreId = questionnaireId.toString();
        const foundControl = this.fQuestionnaireConfiguration.controls.find(
          (formcontrol: AbstractControl) =>
            formcontrol.get('questionnaire_id').value.toString() === qreId,
        );
        if (!foundControl || foundControl.get('unlock_type').value !== 'AFTER_PREVIOUS') {
          return of(false);
        }
        // Previous position based on form_custom_order
        const previousEnabledQuestionnaire =
          this.helper.getPreviousEnabledQuestionnaire(qreId, this.formCustomOrderSubject.value);
        if (previousEnabledQuestionnaire !== undefined) {
          const foundPredecessor = this.fQuestionnaireConfiguration.controls.find(
            (formcontrol: AbstractControl) =>
              formcontrol.get('questionnaire_id').value.toString() === previousEnabledQuestionnaire,
          );
          if (foundPredecessor && foundPredecessor.get('feedback_required').value) {
            return of(true);
          }
        }
        return of(false);
      }),
    );
  }

  getUnlockTypeOfFirst(questionnaireId: number, type: string): string {
    return this.isFirstNotLocked(questionnaireId) && type === 'after_previous' ? 'always' : type;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public lockStartsAt(): boolean {
    return (
      this.instance.attributes.progress.active_questionnaires.length > 0 ||
      this.instance.attributes.progress.finished_questionnaires.length > 0
    );
  }

  public getDefaultConditional(questionnaireId: number) {
    return (
      this.getDefaultConfigurationOfQuestionnaire(
        questionnaireId,
        this.defaultConfigurationReadOnly.questionnaire_configuration
      ) as InterventionConfigurationInterface
    ).condition;
  }

  public getDefaultBonusLesson(questionnaireId: number) {
    return (
      this.getDefaultConfigurationOfQuestionnaire(
        questionnaireId,
        this.defaultConfigurationReadOnly.questionnaire_configuration
      ) as InterventionConfigurationInterface
    ).bonus_lesson;
  }

  // Adding checkboxes on userForm control
  private addCheckboxes() {
    //this.studyCollaborator
    this.selectableCollaborators = this.collaborators.filter(
      user =>
        !!this.helperService.getHighestRoleOfCollaborator(user).match(/^(study.owner|study.ecoachmanager|study.ecoach)$/) ||
        this.helper.isECoachOfInstance(this.instance, user.id)
    );
    this.selectableCollaborators.map((user, i) => {
      const control = new UntypedFormControl();
      (this.instanceFormType.controls.collaboratorList as UntypedFormArray).push(control);
      const isEcoach = this.helper.isECoachOfInstance(this.instance, user.id);
      this.fCollaboratorList.controls[i].setValue(isEcoach);
    });
  }

  // Patch each questionnaire configuration in studyForm
  private editQuestionnaireConfiguration(
    questionnaire: LessonInterface,
    index: number,
    customOrder: Array<{ questionnaire: number; enabled: boolean }>,
    unlockType: string,
    unlockDaysAfterStart: number,
    unlockDate: Date,
    feedbackRequired: boolean,
  ): void {
    // Is lesson enabled - in form_custom_order?
    const foundIndex = customOrder.findIndex(
      (x) => x.questionnaire.toString() === questionnaire.id.toString(),
    );
    const found = foundIndex !== -1 ? customOrder[foundIndex] : undefined;
    this.fQuestionnaireConfiguration.controls[index].get('questionnaire_id').patchValue(questionnaire.id, { onlySelf: true });
    this.fQuestionnaireConfiguration.controls[index].get('current_position').patchValue(foundIndex.toString(), { onlySelf: true });
    this.fQuestionnaireConfiguration.controls[index].get('current_enabled').patchValue(found ? found.enabled : false, { onlySelf: true });
    this.fQuestionnaireConfiguration.controls[index].get('unlock_type').patchValue(unlockType, { onlySelf: true });
    this.fQuestionnaireConfiguration.controls[index].get('unlock_days_after_start').patchValue(unlockDaysAfterStart, { onlySelf: true });
    this.fQuestionnaireConfiguration.controls[index].get('unlock_date').patchValue(unlockDate, { onlySelf: true });
    this.fQuestionnaireConfiguration.controls[index].get('feedback_required').patchValue(feedbackRequired, { onlySelf: true });
    this.fQuestionnaireConfiguration.controls[index]
      .get('condition')
      .patchValue(this.getDefaultConditional(questionnaire.id), { onlySelf: true });
    this.fQuestionnaireConfiguration.controls[index]
      .get('bonus_lesson')
      .patchValue(this.getDefaultBonusLesson(questionnaire.id), { onlySelf: true });
    this.updateView.next('editQuestionnaireConfiguration');
  }

  private calculateUnlockDaysAfterStart(questionnaireId: number, instance: InterventionInstanceInterface): number {
    const customConfiguration: InterventionInstanceConfigurationInterface = this.helperService.findArrObjById(
      questionnaireId,
      instance.attributes.configuration.questionnaire_configuration
    );
    const startingAtTimestamp = instance.attributes.configuration.starting_at;
    const days =
      customConfiguration.unlock_date === null || customConfiguration.unlock_date - startingAtTimestamp < 0
        ? 0
        : (customConfiguration.unlock_date - startingAtTimestamp) / (24 * 60 * 60);
    return Math.round(days);
  }

  private getDefaultConfigurationOfQuestionnaire(
    questionnaireId: number,
    configuration: InterventionConfigurationInterface[],
  ): InterventionConfigurationInterface {
    return configuration.find(
      (questionnaire) => questionnaire.id.toString() === questionnaireId.toString(),
    );
  }

  private getInstanceConfigurationOfQuestionnaire(
    questionnaireId: number,
    configuration: InterventionInstanceConfigurationInterface[],
  ): InterventionInstanceConfigurationInterface {
    const found = configuration.find(
      (questionnaire) => questionnaire.id.toString() === questionnaireId.toString(),
    );
    return found ? found : null;
  }

  private setInitialQuestionnaireConfigControl(questionnaires: LessonInterface[]): void {
    this.fQuestionnaireConfiguration.setValue([]);
    this.formCustomOrderSubject.next(
      this.defaultConfiguration.custom_order
        .map((id) => ({ questionnaire: parseInt(id.toString(), 10), enabled: true })),
    );
    questionnaires.forEach((questionnaire: LessonInterface) => {
      // Is enabled?
      const foundIndex = this.formCustomOrderSubject.value.findIndex(
        index => index.questionnaire.toString() === questionnaire.id.toString(),
      );
      const found = foundIndex > -1;
      const currentPosition = found ? foundIndex : this.formCustomOrderSubject.value.length;

      // If no configuration found in instance then get default configuration of intervention
      let interventionQuestionnaireConfig: InterventionConfigurationInterface = null;
      let instanceQuestionnaireConfig: InterventionInstanceConfigurationInterface = null;
      let unlockType: string;
      let unlockDaysAfterStart: number;
      let feedbackRequired: boolean;
      let unlockDate: Date;

      const hasInstanceConfig =
        this.getInstanceConfigurationOfQuestionnaire(
          questionnaire.id,
          this.instance.attributes.configuration.questionnaire_configuration
        ) !== null;

      if (hasInstanceConfig) {
        instanceQuestionnaireConfig = this.getInstanceConfigurationOfQuestionnaire(
          questionnaire.id,
          this.instance.attributes.configuration.questionnaire_configuration
        );
        unlockType = this.getUnlockTypeOfFirst(questionnaire.id, instanceQuestionnaireConfig.unlock_type).toUpperCase();
        unlockDaysAfterStart = this.calculateUnlockDaysAfterStart(questionnaire.id, this.instance);
        feedbackRequired = instanceQuestionnaireConfig.feedback_required;
        unlockDate = this.getRecalculatedUnlockDate(questionnaire.id, unlockDaysAfterStart);
      } else {
        interventionQuestionnaireConfig = this.getDefaultConfigurationOfQuestionnaire(
          questionnaire.id,
          this.defaultConfigurationReadOnly.questionnaire_configuration
        );
        unlockType = this.getUnlockTypeOfFirst(questionnaire.id, interventionQuestionnaireConfig.unlock_type.toUpperCase());
        unlockDaysAfterStart = interventionQuestionnaireConfig.unlock_days_after_start;
        feedbackRequired = interventionQuestionnaireConfig.feedback_required;
        unlockDate = this.getRecalculatedUnlockDate(questionnaire.id, unlockDaysAfterStart);
      }

      this.fQuestionnaireConfiguration.push(
        this.formBuilder.group({
          questionnaire_id: [questionnaire.id],
          current_position: [currentPosition.toString()],
          current_enabled: [!!found, Validators.required],
          unlock_type: [unlockType],
          unlock_days_after_start: [unlockDaysAfterStart],
          unlock_date: [unlockDate],
          feedback_required: [feedbackRequired],
          condition: null,
          bonus_lesson: null,
        })
      );

      if (!found) {
        const added = this.formCustomOrderSubject.value;
        added.push({ questionnaire: parseInt(questionnaire.id.toString(), 10), enabled: found });
        this.formCustomOrderSubject.next(added);
      }
    });

    this.sortByDefaultCustomOrderPosition();
    this.updateView.next('setInitialQuestionnaireConfigControl');
  }

  private setInstanceQuestionnaireConfig(): void {
    // Custom order from instance
    this.formCustomOrderSubject.next(
      this.instance.attributes.configuration.custom_order
        .map((id) => ({ questionnaire: parseInt(id.toString(), 10), enabled: true })),
    );

    // Set collaborator, patient, starts_at and intervention_type from Instance
    const collaborators = this.getMultipleCollaboratorsFromInstance();
    this.instanceFormType.controls['collaborator'].setValue(collaborators, { onlySelf: true });
    this.instanceFormType.controls['patient'].setValue(
      this.helperService.findArrObjById(this.instance.attributes.patient_id, this.studyMembers),
      { onlySelf: true },
    );
    this.instanceFormType.controls['starts_at'].setValue(
      this.helperService.formatDateToYYYY_MM_DD_THH_MM(this.instance.attributes.configuration.starting_at * 1000)
    );
    this.instanceFormType.controls['intervention_type'].setValue(this.instance.attributes.intervention_type.toUpperCase());

    this.questionnaires.forEach((questionnaire: LessonInterface, i: number) => {
      const foundIndex = this.formCustomOrderSubject.value.findIndex(
        index => index.questionnaire.toString() === questionnaire.id.toString()
      );
      const found = foundIndex > -1;

      // If no configuration found in instance then get default configuration of intervention
      let interventionQuestionnaireConfig: InterventionConfigurationInterface = null;
      let instanceQuestionnaireConfig: InterventionInstanceConfigurationInterface = null;
      let unlockType: string;
      let unlockDaysAfterStart: number;
      let feedbackRequired: boolean;
      let unlockDate: Date;

      const hasInstanceConfig =
        this.getInstanceConfigurationOfQuestionnaire(
          questionnaire.id,
          this.instance.attributes.configuration.questionnaire_configuration
        ) !== null;
      if (hasInstanceConfig) {
        instanceQuestionnaireConfig = this.getInstanceConfigurationOfQuestionnaire(
          questionnaire.id,
          this.instance.attributes.configuration.questionnaire_configuration
        );
        unlockType = this.getUnlockTypeOfFirst(questionnaire.id, instanceQuestionnaireConfig.unlock_type).toUpperCase();
        unlockDaysAfterStart = this.calculateUnlockDaysAfterStart(questionnaire.id, this.instance);
        feedbackRequired = instanceQuestionnaireConfig.feedback_required;
        unlockDate = this.getRecalculatedUnlockDate(questionnaire.id, unlockDaysAfterStart);
      } else {
        interventionQuestionnaireConfig = this.getDefaultConfigurationOfQuestionnaire(
          questionnaire.id,
          this.defaultConfigurationReadOnly.questionnaire_configuration
        );
        unlockType = this.getUnlockTypeOfFirst(questionnaire.id, interventionQuestionnaireConfig.unlock_type).toUpperCase();
        unlockDaysAfterStart = interventionQuestionnaireConfig.unlock_days_after_start;
        feedbackRequired = interventionQuestionnaireConfig.feedback_required;
        unlockDate = this.getRecalculatedUnlockDate(questionnaire.id, unlockDaysAfterStart);
      }

      if (!found) {
        const added = this.formCustomOrderSubject.value;
        added.push({ questionnaire: parseInt(questionnaire.id.toString(), 10), enabled: found });
        this.formCustomOrderSubject.next(added);
      }

      this.editQuestionnaireConfiguration(
        questionnaire,
        i,
        this.formCustomOrderSubject.value,
        unlockType,
        unlockDaysAfterStart,
        unlockDate,
        feedbackRequired
      );
    });
    this.sortByDefaultCustomOrderPosition();
    this.updateView.next('setInstanceQuestionnaireConfig');
  }

  private getMultipleCollaboratorsFromInstance(): UserInterface[] {
    const collaborators: UserInterface[] = [];
    if (Array.isArray(this.instance.attributes.ecoach_id)) {
      this.instance.attributes.ecoach_id.forEach((id: number) => {
        const found = this.helperService.findArrObjById(id, this.studyCollaborator);
        if (found !== null) {
          collaborators.push(found);
        }
      });
    }
    return collaborators;
  }

  private setMinDate(): void {
    const startsAt = this.instance.attributes.configuration.starting_at * 1000;
    const now = new Date();
    if (startsAt > now.getTime()) {
      this.minDate = now;
    } else {
      this.minDate = new Date(startsAt);
    }
  }
}
